import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Period, PeriodPreset } from '../../types/timerocket';
import { formatRange } from './format';

interface Props {
    period: Period;
    /** False once the period already reaches today — there is nothing beyond it to show. */
    canShiftForward: boolean;
    onPreset: (preset: PeriodPreset) => void;
    onShift: (direction: 1 | -1) => void;
    onCustom: (from: string, to: string) => void;
}

const PRESETS: { key: PeriodPreset; label: string }[] = [
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' },
    { key: 'custom', label: 'Custom' },
];

/**
 * The period the whole page is about: a preset (month / quarter / year, stepped with the
 * chevrons) or a free range. The resolved range is always spelled out next to the buttons, so
 * "Month" never leaves the reader guessing whether it means the calendar month or the last 30
 * days — it is the calendar month, cut at today.
 */
export function PeriodPicker({ period, canShiftForward, onPreset, onShift, onCustom }: Props) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded border border-gray-300 overflow-hidden text-xs" role="group">
                {PRESETS.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => onPreset(p.key)}
                        className={`px-2.5 py-1.5 font-medium transition-colors ${
                            period.preset === p.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="inline-flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => onShift(-1)}
                    title="Previous period"
                    className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => onShift(1)}
                    disabled={!canShiftForward}
                    title={canShiftForward ? 'Next period' : 'The period already reaches today'}
                    className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>

            {period.preset === 'custom' ? (
                <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                        type="date"
                        value={period.from}
                        max={period.to}
                        onChange={e => onCustom(e.target.value, period.to)}
                        className="h-7 rounded border border-gray-300 px-1.5 text-sm"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                        type="date"
                        value={period.to}
                        min={period.from}
                        onChange={e => onCustom(period.from, e.target.value)}
                        className="h-7 rounded border border-gray-300 px-1.5 text-sm"
                    />
                </div>
            ) : (
                <span className="text-sm font-medium text-gray-800 tabular-nums">{formatRange(period.from, period.to)}</span>
            )}
        </div>
    );
}
